import { createApi } from '@reduxjs/toolkit/query/react';
import { waServicesQuery } from './baseQueries';

// This interface defines the expected shape of the configuration for a set of tabs.
export interface TabSettings {
  // An ordered array of tab IDs to determine their display sequence.
  order?: string[];
  // An array of tab IDs that should be hidden from view.
  hidden?: string[];
  // The ID of the tab that should be selected by default when the component loads.
  default?: string;
}

// This interface represents a single tab configuration record from the legacy API.
interface LegacyTabRecord {
  TabUIKey: string;
  Label: string;
  IsEnabled: boolean;
  Order: number;
}

// This interface represents the raw response from the /api/user/tabs endpoint.
interface LegacyTabsResponse {
  Records: LegacyTabRecord[];
  TotalLength: number;
}

export const tabsApi = createApi({
  reducerPath: 'tabsApi',
  baseQuery: waServicesQuery,
  endpoints: (builder) => ({
    // Endpoint to fetch settings for a specific tab group, identified by a key.
    getTabSettings: builder.query<TabSettings, string>({
      queryFn: async (tabKey, { getState }, __, baseQuery) => {
        const result = await baseQuery({
          url: `/api/user/tabs`,
          params: {
            // The legacy API uses `tabPanelKey` as the parameter for the tab group identifier.
            tabPanelKey: tabKey,
          },
        });

        if (result.error) {
          // If the API call fails, we can return an empty configuration
          // to allow the component to fall back to its default, hardcoded tabs.
          return { data: { order: [], hidden: [] } };
        }

        // The data from the legacy API needs to be transformed into the modern TabSettings shape.
        const legacyData = result.data as LegacyTabsResponse;
        const records = legacyData.Records || [];

        // Tabs with IsEnabled: false are considered hidden.
        const hidden = records
          .filter((tab) => !tab.IsEnabled)
          .map((tab) => tab.TabUIKey);

        // Enabled tabs are sorted by the 'Order' property to determine their display sequence.
        const order = records
          .filter((tab) => tab.IsEnabled)
          .sort((a, b) => a.Order - b.Order)
          .map((tab) => tab.TabUIKey);

        // The legacy API for /api/user/tabs does not provide a default tab setting.
        // This will need to be handled client-side if required.
        const tabSettings: TabSettings = {
          hidden,
          order,
        };

        return { data: tabSettings };
      },
    }),
    // New endpoint to fetch full tab records with labels
    getTabRecords: builder.query<LegacyTabRecord[], string>({
      queryFn: async (tabKey, { getState }, __, baseQuery) => {
        const result = await baseQuery({
          url: `/api/user/tabs`,
          params: {
            tabPanelKey: tabKey,
          },
        });

        if (result.error) {
          return { data: [] };
        }

        const legacyData = result.data as LegacyTabsResponse;
        const records = legacyData.Records || [];

        // Filter enabled tabs and sort by order
        const enabledTabs = records
          .filter((tab) => tab.IsEnabled)
          .sort((a, b) => a.Order - b.Order);

        return { data: enabledTabs };
      },
    }),
  }),
});

export const { useGetTabSettingsQuery, useGetTabRecordsQuery } = tabsApi;
